/**
 * Un proveedor — `OD-0054 D-02` y `D-05`.
 *
 * El proveedor se identifica por su NIF, que no se cambia: con un NIF que ya
 * está, el alta devuelve ese proveedor en vez de duplicarlo. No se borra; se
 * archiva y deja de ofrecerse al anotar.
 *
 * Aquí vive el PASO 1 del anticipo (`D-05`): «Apuntar pago sin factura», el
 * dinero que se adelanta antes de tener la factura. Mientras no llega, la
 * ficha lo dice: sin factura no se puede deducir el IVA, y a partir del día 16
 * del mes siguiente el proveedor ya llega tarde (lo calcula la API).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { apiCompras, ESTADO_VALIDACION, METODOS_COBRO, type ErrorApi, type FichaProveedor, type MetodoCobro, type PresupuestoEnlazado } from "../api/cliente";
import { t } from "../i18n/es";
import { EnlacesComprobarAeat } from "../ui/EnlacesComprobarAeat";
import { importeLegible, sinPuntosDeMiles } from "../ui/formato";
import { AreaTexto, Aviso, Boton, Campo, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import { SelectorPresupuesto } from "./PantallaCompra";
import { AvisoEscrituraCompras, InsigniaPago } from "./PantallaCompras";
import { diaLegible } from "./PantallaOportunidades";

function hoy(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
}

type Mensaje = { readonly tono: "exito" | "aviso" | "error" | "info"; readonly texto: string };

function rechazo(r: { estado: number; error: ErrorApi | null }): Mensaje {
  if (r.estado === 0) return { tono: "error", texto: t.compras.sinConexion };
  if (r.estado === 402) return { tono: "info", texto: t.compras.soloBusiness };
  if (r.estado === 403) return { tono: "aviso", texto: t.compras.sinPermisoEscribir };
  if (r.estado === 409 && r.error !== null) return { tono: "aviso", texto: r.error.action === null ? r.error.message : `${r.error.message} ${r.error.action}` };
  return { tono: "error", texto: t.compras.error };
}

const nulo = (v: string): string | null => (v.trim() === "" ? null : v.trim());

function AltaProveedor({
  hayConexion,
  onCreado,
  onCancelar,
  onSesionCaducada,
}: {
  hayConexion: boolean;
  onCreado: (id: string, yaExistia: boolean) => void;
  onCancelar: () => void;
  onSesionCaducada: () => void;
}) {
  const [nif, setNif] = useState("");
  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [notas, setNotas] = useState("");
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const errorDe = (k: string): string | null => (errores[k] === undefined ? null : (t.compras.errores[errores[k]] ?? t.comun.errorGenerico));

  async function crear(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.crearProveedor({ taxId: nif.trim(), name: nombre.trim(), email: nulo(correo), notes: nulo(notas) });
    setOcupado(false);
    if (r.ok) return onCreado(r.datos.proveedor.id, !r.datos.creado);
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
    setMensaje(rechazo(r));
  }

  return (
    <Tarjeta titulo={t.compras.nuevoProveedor}>
      <Campo id="proveedor-nif" etiqueta={t.compras.nif} valor={nif} onChange={setNif} ayuda={t.compras.ayudaNif} error={errorDe("taxId")} />
      <Campo id="proveedor-nombre" etiqueta={t.compras.nombre} valor={nombre} onChange={setNombre} error={errorDe("name")} />
      <Campo id="proveedor-correo" etiqueta={t.compras.correo} tipo="email" requerido={false} valor={correo} onChange={setCorreo} error={errorDe("email")} />
      <AreaTexto id="proveedor-notas" etiqueta={t.compras.notas} requerido={false} valor={notas} onChange={setNotas} error={errorDe("notes")} />
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      <div className="acciones">
        <Boton onClick={() => void crear()} deshabilitado={ocupado || !hayConexion}>
          {ocupado ? t.compras.guardando : t.compras.crearProveedor}
        </Boton>
        <Boton variante="secundario" onClick={onCancelar}>
          {t.compras.cancelar}
        </Boton>
      </div>
    </Tarjeta>
  );
}

function PagoSinFacturaForm({
  proveedorId,
  presupuestos,
  hayConexion,
  onHecho,
  onSesionCaducada,
}: {
  proveedorId: string;
  /** Los aceptados del proveedor: el anticipo de un presupuesto se apunta contra él (`OD-0055 D-06`). */
  presupuestos: readonly PresupuestoEnlazado[];
  hayConexion: boolean;
  onHecho: (m: Mensaje) => void;
  onSesionCaducada: () => void;
}) {
  const [importe, setImporte] = useState("");
  const [fecha, setFecha] = useState(hoy);
  const [metodo, setMetodo] = useState<MetodoCobro>("TRANSFERENCIA");
  const [nota, setNota] = useState("");
  const [presupuesto, setPresupuesto] = useState("");
  // La misma clave en los reintentos de UN apunte: dos pulsaciones no apuntan dos pagos.
  // Tras apuntarlo la ficha se pinta de nuevo, y el formulario nuevo trae otra.
  const [clave] = useState(() => crypto.randomUUID());
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const errorDe = (k: string): string | null => (errores[k] === undefined ? null : (t.compras.errores[errores[k]] ?? t.comun.errorGenerico));

  async function apuntar(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.pagoSinFactura(proveedorId, { amount: sinPuntosDeMiles(importe), paidOn: fecha, method: metodo, note: nulo(nota), supplierQuoteId: presupuesto === "" ? null : presupuesto }, clave);
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
      return setMensaje(rechazo(r));
    }
    onHecho({ tono: "exito", texto: t.compras.pagoSinFacturaAnotado });
  }

  return (
    <div className="grupo-campos">
      <h3>{t.compras.apuntarPagoSinFactura}</h3>
      <p className="ayuda-neutra">{t.compras.ayudaPagoSinFactura}</p>
      <Campo id="proveedor-pago-importe" etiqueta={t.compras.importe} valor={importe} onChange={setImporte} ayuda={t.compras.ayudaImporte} error={errorDe("amount")} />
      <Campo id="proveedor-pago-fecha" etiqueta={t.compras.fechaPago} tipo="date" valor={fecha} onChange={setFecha} error={errorDe("paidOn")} />
      <Selector
        id="proveedor-pago-metodo"
        etiqueta={t.compras.metodo}
        valor={metodo}
        opciones={METODOS_COBRO.map((m) => ({ valor: m, texto: t.cobros.metodos[m] ?? m }))}
        onChange={setMetodo}
        error={errorDe("method")}
      />
      <SelectorPresupuesto
        id="proveedor-pago-presupuesto"
        etiqueta={t.compras.presupuestoDelPago}
        ayuda={t.compras.ayudaPresupuestoPago}
        presupuestos={presupuestos}
        valor={presupuesto}
        onChange={setPresupuesto}
        error={errorDe("supplierQuoteId")}
      />
      <AreaTexto id="proveedor-pago-nota" etiqueta={t.compras.nota} requerido={false} valor={nota} onChange={setNota} error={errorDe("note")} />
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      <Boton onClick={() => void apuntar()} deshabilitado={ocupado || !hayConexion}>
        {ocupado ? t.compras.guardando : t.compras.apuntarPago}
      </Boton>
    </div>
  );
}

function Ficha({
  ficha,
  hayConexion,
  mensajeInicial,
  onRecargar,
  onAbrirCompra,
  onNuevaCompra,
  onVerPlan,
  onSesionCaducada,
}: {
  ficha: FichaProveedor;
  hayConexion: boolean;
  mensajeInicial: Mensaje | null;
  /** Recarga la ficha, que se pinta de nuevo con este mensaje. */
  onRecargar: (m: Mensaje) => void;
  onAbrirCompra: (id: string) => void;
  onNuevaCompra: (proveedorId: string) => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const p = ficha.proveedor;
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(p.name);
  const [correo, setCorreo] = useState(p.email ?? "");
  const [notas, setNotas] = useState(p.notes ?? "");
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(mensajeInicial);
  const [ocupado, setOcupado] = useState(false);
  const [anulando, setAnulando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const errorDe = (k: string): string | null => (errores[k] === undefined ? null : (t.compras.errores[errores[k]] ?? t.comun.errorGenerico));

  async function guardar(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.editarProveedor(p.id, { name: nombre.trim(), email: nulo(correo), notes: nulo(notas) });
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
      return setMensaje(rechazo(r));
    }
    onRecargar({ tono: "exito", texto: t.compras.proveedorGuardado });
  }

  async function archivar(archivar: boolean): Promise<void> {
    setOcupado(true);
    setMensaje(null);
    const r = await apiCompras.archivarProveedor(p.id, archivar);
    setOcupado(false);
    if (!r.ok) return r.estado === 401 ? onSesionCaducada() : setMensaje(rechazo(r));
    onRecargar({ tono: "exito", texto: archivar ? t.compras.proveedorArchivado : t.compras.proveedorRecuperado });
  }

  async function anularPago(id: string): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await apiCompras.anularPago(id, motivo);
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores({ motivo: r.error.campos.reason ?? "REQUIRED" });
      return setMensaje(rechazo(r));
    }
    onRecargar({ tono: "exito", texto: t.compras.pagoAnulado });
  }

  const activo = p.status === "ACTIVE";
  const puede = ficha.writable;
  return (
    <Tarjeta titulo={p.name}>
      <AvisoEscrituraCompras bloqueo={ficha.bloqueo} onVerPlan={onVerPlan} />
      <p>
        {p.taxId}
        {!activo ? (
          <>
            {" "}
            <span className="insignia">{t.compras.archivado}</span>
          </>
        ) : null}
      </p>
      <EnlacesComprobarAeat tipo={p.taxIdType} />
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      {editando ? (
        <div className="grupo-campos">
          <Campo id="proveedor-editar-nombre" etiqueta={t.compras.nombre} valor={nombre} onChange={setNombre} error={errorDe("name")} />
          <Campo id="proveedor-editar-correo" etiqueta={t.compras.correo} tipo="email" requerido={false} valor={correo} onChange={setCorreo} error={errorDe("email")} />
          <AreaTexto id="proveedor-editar-notas" etiqueta={t.compras.notas} requerido={false} valor={notas} onChange={setNotas} error={errorDe("notes")} />
          <p className="ayuda-neutra">{t.compras.nifNoCambia}</p>
          <div className="acciones">
            <Boton onClick={() => void guardar()} deshabilitado={ocupado || !hayConexion}>
              {t.compras.guardar}
            </Boton>
            <Boton variante="secundario" onClick={() => setEditando(false)}>
              {t.compras.cancelar}
            </Boton>
          </div>
        </div>
      ) : (
        <dl className="datos">
          {p.email !== null ? (
            <div>
              <dt>{t.compras.datos.correo}</dt>
              <dd>{p.email}</dd>
            </div>
          ) : null}
          {p.notes !== null ? (
            <div>
              <dt>{t.compras.datos.notas}</dt>
              <dd>{p.notes}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t.compras.pendiente}</dt>
            <dd>{importeLegible(ficha.pendiente)} €</dd>
          </div>
        </dl>
      )}

      {puede && !editando ? (
        <div className="acciones">
          {activo ? (
            <Boton onClick={() => onNuevaCompra(p.id)} deshabilitado={!hayConexion}>
              {t.compras.anotar}
            </Boton>
          ) : null}
          <Boton variante="secundario" onClick={() => setEditando(true)}>
            {t.compras.editar}
          </Boton>
          <Boton variante="terciario" onClick={() => void archivar(activo)} deshabilitado={ocupado || !hayConexion}>
            {activo ? t.compras.archivar : t.compras.recuperar}
          </Boton>
        </div>
      ) : null}

      {/* D-05: el aviso de cada pago sin factura, con el plazo que calcula la API. */}
      {ficha.sinFactura.length > 0 ? (
        <section aria-label={t.compras.sinFacturaTitulo}>
          <h3>{t.compras.sinFacturaTitulo}</h3>
          {ficha.sinFactura.map((x) => (
            <Aviso key={x.id} tono={x.late ? "aviso" : "info"}>
              {t.compras.pagoSinFacturaLinea(importeLegible(x.amount), diaLegible(x.paidOn))}
              {x.quoteNumber !== null ? ` ${t.compras.delPresupuesto(x.quoteNumber)}` : ""} {t.compras.faltaFactura}
              {x.late ? ` ${t.compras.proveedorTarde}` : ""}
            </Aviso>
          ))}
          {puede && activo ? <p className="ayuda-neutra">{t.compras.comoAsignar}</p> : null}
        </section>
      ) : null}

      {puede && activo ? <PagoSinFacturaForm proveedorId={p.id} presupuestos={ficha.presupuestosAceptados} hayConexion={hayConexion} onHecho={onRecargar} onSesionCaducada={onSesionCaducada} /> : null}

      <h3>{t.compras.facturasDelProveedor}</h3>
      {ficha.compras.length === 0 ? <p className="ayuda-neutra">{t.compras.sinFacturasProveedor}</p> : null}
      {ficha.compras.length > 0 ? (
        <ul className="lista-filas">
          {ficha.compras.map((c) => (
            <FilaPulsable key={c.id} onClick={() => onAbrirCompra(c.id)}>
              <span className={c.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>
                {c.registryNumber} · {t.compras.suFactura(c.supplierInvoiceNumber)}
              </span>
              <span className="ayuda-neutra">
                {diaLegible(c.issueDate)} · {t.compras.tipos[c.kind]}
                {c.status === "VOIDED" ? ` · ${t.compras.anulada}` : ""}
              </span>
              {c.status === "REGISTERED" ? (
                <span className="ayuda-neutra">
                  <InsigniaPago estado={c.paymentStatus} /> · {t.compras.deTotal(importeLegible(c.pending), importeLegible(c.payable))}
                </span>
              ) : null}
            </FilaPulsable>
          ))}
        </ul>
      ) : null}

      {ficha.pagos.length > 0 ? (
        <section aria-label={t.compras.todosLosPagos}>
          <h3>{t.compras.todosLosPagos}</h3>
          <ul className="lista-filas">
            {ficha.pagos.map((x) => (
              <li key={x.id} className="fila">
                <span className="fila-texto">
                  <span className={x.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>{importeLegible(x.amount)} €</span>
                  <span className="ayuda-neutra">
                    {diaLegible(x.paidOn)} · {t.cobros.metodos[x.method]} · {x.purchaseId === null ? t.compras.sinFacturaCorto : t.compras.conFactura}
                  </span>
                  {x.status === "VOIDED" ? <span className="ayuda-neutra">{t.compras.anuladoPor(x.voidReason ?? "")}</span> : null}
                </span>
                {puede && x.status === "POSTED" && anulando !== x.id ? (
                  <Boton variante="terciario" onClick={() => setAnulando(x.id)} deshabilitado={ocupado || !hayConexion}>
                    {t.compras.anularPago}
                  </Boton>
                ) : null}
              </li>
            ))}
          </ul>
          {anulando !== null ? (
            <div>
              <Campo id="proveedor-pago-motivo" etiqueta={t.compras.motivoAnularPago} valor={motivo} onChange={setMotivo} error={errorDe("motivo")} />
              <div className="acciones">
                <Boton variante="peligro" onClick={() => void anularPago(anulando)} deshabilitado={ocupado || !hayConexion}>
                  {t.compras.confirmarAnularPago}
                </Boton>
                <Boton variante="secundario" onClick={() => setAnulando(null)}>
                  {t.compras.cancelar}
                </Boton>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </Tarjeta>
  );
}

type Vista =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "no-encontrado" }
  | { readonly tipo: "ficha"; readonly ficha: FichaProveedor; readonly mensaje: Mensaje | null; readonly version: number };

export function PantallaProveedor({
  id,
  hayConexion,
  onVolver,
  onAbrirCompra,
  onNuevaCompra,
  onVerPlan,
  onSesionCaducada,
}: {
  id: string | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrirCompra: (id: string) => void;
  onNuevaCompra: (proveedorId: string) => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const [actual, setActual] = useState<string | null>(id);
  const [vista, setVista] = useState<Vista>({ tipo: "cargando" });
  // El recién creado lo carga el alta, con su mensaje: el efecto no lo vuelve a pedir.
  const yaPedido = useRef<string | null>(null);

  const cargar = useCallback(
    async (proveedorId: string, mensaje: Mensaje | null, version: number): Promise<void> => {
      const r = await apiCompras.obtenerProveedor(proveedorId);
      if (r.ok) return setVista({ tipo: "ficha", ficha: r.datos, mensaje, version });
      if (r.estado === 401) return onSesionCaducada();
      setVista({ tipo: r.estado === 404 ? "no-encontrado" : "error" });
    },
    [onSesionCaducada],
  );

  useEffect(() => {
    if (actual === null || yaPedido.current === actual) return;
    void cargar(actual, null, 0);
  }, [actual, cargar]);

  const volver = (
    <div className="barra-acciones">
      <Boton variante="secundario" onClick={onVolver}>
        {t.compras.volverProveedores}
      </Boton>
    </div>
  );

  if (actual === null) {
    return (
      <>
        {volver}
        <AltaProveedor
          hayConexion={hayConexion}
          onCreado={(nuevo, yaExistia) => {
            yaPedido.current = nuevo;
            setActual(nuevo);
            void cargar(nuevo, yaExistia ? { tono: "info", texto: t.compras.yaExistia } : { tono: "exito", texto: t.compras.proveedorCreado }, 0);
          }}
          onCancelar={onVolver}
          onSesionCaducada={onSesionCaducada}
        />
      </>
    );
  }

  return (
    <>
      {volver}
      {vista.tipo === "cargando" ? <p>{t.compras.cargando}</p> : null}
      {vista.tipo === "no-encontrado" ? <Aviso tono="aviso">{t.compras.proveedorNoEncontrado}</Aviso> : null}
      {vista.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.compras.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar(actual, null, 0)}>
            {t.compras.reintentar}
          </Boton>
        </>
      ) : null}
      {vista.tipo === "ficha" ? (
        <Ficha
          // Cada recarga, una ficha nueva: los formularios se vacían con lo que dice la API.
          key={`${vista.ficha.proveedor.id}-${vista.version}`}
          ficha={vista.ficha}
          hayConexion={hayConexion}
          mensajeInicial={vista.mensaje}
          onRecargar={(m) => void cargar(actual, m, vista.version + 1)}
          onAbrirCompra={onAbrirCompra}
          onNuevaCompra={onNuevaCompra}
          onVerPlan={onVerPlan}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
    </>
  );
}
